import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, BookOpen, Clock3, MessageCircle, Phone } from "lucide-react";
import { ARTICLES } from "../data/articles";
import { SERVICE_PAGES } from "../data/services";
import { CONTACT_PHONE, CONTACT_PHONE_DISPLAY, WHATSAPP_LINK } from "../data/site";

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};

const setMeta = (title, desc) => {
  document.title = title;
  document.querySelector('meta[name="description"]')?.setAttribute("content", desc);
};

const GuideHeader = () => (
  <header className="sp-header">
    <div className="container header-inner">
      <Link to="/" className="brand" data-testid="guide-brand-link">
        <img className="brand-logo" src="/logo.png" alt="Nova Teknik Servis Düzce" />
      </Link>
      <div className="header-actions">
        <span className="status-badge"><span className="pulse-dot" /> AÇIK · 08:00–20:00</span>
        <a className="btn-call" data-testid="guide-call-button" href={`tel:${CONTACT_PHONE}`}>
          <Phone size={15} /> <span className="call-text">{CONTACT_PHONE_DISPLAY}</span>
        </a>
      </div>
    </div>
  </header>
);

export function GuideList() {
  useEffect(() => {
    window.scrollTo(0, 0);
    setMeta("Arıza Rehberi | Nova Teknik Düzce", "Beyaz eşya ve klima arızaları hakkında pratik rehberler: çamaşır makinesi su boşaltmıyor, buzdolabı soğutmuyor ve daha fazlası. Nova Teknik Düzce.");
  }, []);

  return (
    <div className="site-shell" data-testid="guide-page">
      <div className="noise-overlay" aria-hidden="true" />
      <GuideHeader />
      <main className="container guide-main">
        <motion.div initial="hidden" animate="show" variants={fadeUp}>
          <Link to="/" className="sp-back" data-testid="guide-back-home"><ArrowLeft size={14} /> Ana sayfa</Link>
          <div className="kicker">ARIZA REHBERİ</div>
          <h1 className="sp-h1">Servis çağırmadan önce<br /><span className="accent">buraya bakın.</span></h1>
          <p className="hero-lede">En sık karşılaştığımız arızaları ve servis öncesi kendinizin yapabileceği güvenli kontrolleri derledik.</p>
        </motion.div>
        <div className="guide-grid">
          {ARTICLES.map((a, i) => (
            <motion.div key={a.slug} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i % 2}>
              <Link to={`/rehber/${a.slug}`} className="guide-card" data-testid={`guide-card-${i}`}>
                <img src={a.image} alt={a.title} loading="lazy" />
                <div className="guide-card-body">
                  <span className="guide-meta"><BookOpen size={13} /> {a.readTime} okuma · {a.date}</span>
                  <h3>{a.title}</h3>
                  <p>{a.excerpt}</p>
                  <span className="service-link">Yazıyı oku <ArrowUpRight size={14} /></span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </main>
      <a className="float-whatsapp" data-testid="float-whatsapp-button" href={WHATSAPP_LINK} target="_blank" rel="noreferrer" aria-label="WhatsApp">
        <MessageCircle size={22} />
      </a>
    </div>
  );
}

export function ArticlePage() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (article) setMeta(article.meta.split(".")[0] + " | Nova Teknik", article.meta);
  }, [article]);

  if (!article) return <Navigate to="/rehber" replace />;

  const related = SERVICE_PAGES.find((s) => s.slug === article.service);
  const otherArticles = ARTICLES.filter((a) => a.slug !== slug).slice(0, 2);

  return (
    <div className="site-shell" data-testid="article-page">
      <div className="noise-overlay" aria-hidden="true" />
      <GuideHeader />
      <main className="container article-main">
        <motion.div initial="hidden" animate="show" variants={fadeUp}>
          <Link to="/rehber" className="sp-back" data-testid="article-back-guide"><ArrowLeft size={14} /> Arıza rehberi</Link>
          <span className="guide-meta"><BookOpen size={13} /> {article.readTime} okuma · {article.date}</span>
          <h1 className="sp-h1" data-testid="article-title">{article.title}</h1>
          <img className="article-hero" src={article.image} alt={article.title} data-testid="article-image" />
        </motion.div>
        <div className="article-body" data-testid="article-body">
          {article.sections.map((s, i) => (
            <motion.section key={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} variants={fadeUp}>
              <h2>{s.h}</h2>
              {s.p.map((text, j) => <p key={j}>{text}</p>)}
            </motion.section>
          ))}
        </div>
        <div className="sp-cta" data-testid="article-cta">
          <div>
            <h2>Sorun çözülmediyse bize bırakın.</h2>
            <p>{related ? `${related.h1} için aynı gün yerinde servis.` : "Düzce Merkez'e aynı gün yerinde servis."}</p>
          </div>
          <div className="hero-actions">
            <a className="btn-primary" data-testid="article-call-link" href={`tel:${CONTACT_PHONE}`}><Phone size={16} /> Hemen Ara</a>
            {related && (
              <Link className="btn-ghost" data-testid="article-service-link" to={`/${related.slug}`}>
                {related.title} sayfası <ArrowUpRight size={15} />
              </Link>
            )}
          </div>
        </div>
        <div className="sp-related" data-testid="article-related">
          <div className="kicker">DİĞER REHBERLER</div>
          <div className="local-chips">
            {otherArticles.map((a, i) => (
              <Link key={a.slug} to={`/rehber/${a.slug}`} data-testid={`article-related-${i}`}>{a.title.toUpperCase()}</Link>
            ))}
          </div>
        </div>
      </main>
      <a className="float-whatsapp" data-testid="float-whatsapp-button" href={WHATSAPP_LINK} target="_blank" rel="noreferrer" aria-label="WhatsApp">
        <MessageCircle size={22} />
      </a>
    </div>
  );
}
